import type React from 'react';
import {
  AlertTriangle,
  BookOpen,
  LayoutDashboard,
  Plug,
  ShieldCheck,
  ShoppingBasket,
  Truck,
  UserCog,
  Webhook,
  Workflow,
} from 'lucide-react';
import { failLoud } from '@resource-kit/core';
import { PERMISSIONS, type PermissionKey } from '@/permissions';
import { APP_ACTION_MENU_ITEMS } from '@/actions/catalog';
import { MENU_SECTION, type MenuSection } from '@/actions/menu-sections';
import { navNamespacesOf } from './nav-groups';

export type SidebarPermissionKey = PermissionKey;

export interface SidebarNavItem {
  titleKey: string;
  href: string;
  icon: React.ReactNode;
  permission?: SidebarPermissionKey;
  /**
   * Dead for EVERY seat (no backend surface exists or ever will), as opposed
   * to merely ungranted for this seat — which `Sidebar.tsx` renders disabled
   * from `permission` alone. No row carries this today: #1777 retired the last
   * one (系统设置). The field stays because "permanently dead but still worth
   * showing" is a real future state the resolver already honours; re-adding a
   * row with it, however, would undo #1777.
   */
  disabled?: true;
  /**
   * The route behind `href` is wrapped in `<AdminGuard>` (role-gated), so
   * holding `permission` alone is not enough to enter it.
   *
   * Read by `resolveLandingPath` only — it must never emit a route the caller
   * would immediately be bounced out of. Rendering deliberately ignores this
   * flag: such a row keeps rendering exactly as its `permission` dictates
   * (the row-vs-route gate mismatch is pre-existing information architecture,
   * not this marker's business).
   */
  routeAdminOnly?: true;
  /**
   * Every URL namespace this row owns: its own href, plus every tab route when
   * the row is a merged entry (`nav-groups.ts`). Filled by `sectionRows` from
   * `navNamespacesOf(href)`, never written at a declaration.
   */
  namespaces: readonly string[];
  /**
   * The row carries the operator inbox's pending count (#2476 S4). A flag, not
   * a string naming a counter: the inbox row is the only one with a count.
   */
  pendingReviewCount?: true;
}

export interface SidebarSection {
  labelKey: string | null;
  adminOnly?: true;
  items: SidebarNavItem[];
}

/**
 * A row this file still declares itself: one that no action backs, because it
 * is not a resource surface (the dispatch console, the packing slips, the admin
 * pages, the docs). It carries an explicit `order` so it interleaves with the
 * derived rows instead of being appended after them.
 */
interface StaticNavRow extends Omit<SidebarNavItem, 'namespaces'> {
  order: number;
}

/**
 * Build one section's rows: the rows DERIVED from the action layer (#1798) for
 * that section, merged with the static rows above, sorted by `order`.
 *
 * Nothing here decides menu content or grouping — the sections, their labels and
 * the row order are #1775 / #1777's, and `actionLayerSnapshot.test.ts` compares
 * the result against a snapshot of what they shipped, row by row.
 */
function sectionRows(section: MenuSection, staticRows: readonly StaticNavRow[]): SidebarNavItem[] {
  const derived: StaticNavRow[] = APP_ACTION_MENU_ITEMS.filter(
    (item) => item.section === section,
  ).map((item) => ({
    order: item.order,
    titleKey: item.labelKey,
    href: item.href,
    icon: item.icon,
    permission: item.permission,
  }));

  const all = [...derived, ...staticRows].sort((a, b) => a.order - b.order);

  const orders = new Set(all.map((row) => row.order));
  if (orders.size !== all.length) {
    failLoud(
      `sidebar section "${section}": two rows claim the same order, so their order is undefined`,
    );
  }

  return all.map(({ order: _order, ...row }) => ({
    ...row,
    namespaces: navNamespacesOf(row.href),
  }));
}

// 工作台 / Workbench (#2511, owner 2026-10-04) — the surfaces that answer "how
// is it going overall": the ungated overview, the operator inbox and 数据分析
// (derived from the analytics action, order 50).
// The webhook-failure inbox is not a row here: it is a tab of the admin
// 管理 › Webhooks entry (#1364 IA merge, then the v4 shell of #2476).
// `/admin/review-queue` is an `<AdminGuard>` route (the backend surface is
// admin-only), while this row is gated on `review:read` — a key the Users
// permission grid really can hand to an operator. `routeAdminOnly` keeps the
// landing resolver from sending such an operator somewhere the role guard
// bounces; rendering is unchanged.
const workbenchItems: SidebarNavItem[] = sectionRows(MENU_SECTION.workbench, [
  // Ungated (#2456): each overview card checks its own permission. A row
  // without `permission` is invisible to `resolveLandingPath`, so the landing
  // page is unchanged.
  {
    order: 1,
    titleKey: 'nav.overview',
    href: '/overview',
    icon: <LayoutDashboard className="w-4 h-4" />,
  },
  {
    order: 40,
    titleKey: 'nav.reviewQueue',
    href: '/admin/review-queue',
    icon: <AlertTriangle className="w-4 h-4" />,
    permission: PERMISSIONS.REVIEW_READ,
    routeAdminOnly: true,
    pendingReviewCount: true,
  },
]);

// 配送 / Delivery (#2511) — the delivery work and its resources: the dispatch
// console the day is driven from, the full delivery-job list (search and
// cross-day lookup; the console's list view only jumps to the selected day)
// and the merged 司机与司机画像 entry, whose page carries a 司机 | 司机画像 tab
// strip. The jobs and drivers rows are derived from their actions; the console
// is not a resource surface, so it is declared here. Landing priority does not
// depend on this group: `resolveLandingPath` sends any `dispatch:read` seat to
// `/dispatch` before it walks sections at all.
const deliveryItems: SidebarNavItem[] = sectionRows(MENU_SECTION.delivery, [
  {
    order: 5,
    titleKey: 'nav.dispatch',
    href: '/dispatch',
    icon: <Truck className="w-4 h-4" />,
    permission: PERMISSIONS.DISPATCH_READ,
  },
]);

// 订单与商品 / Orders & products (#2511) — the sales-side data: every row is
// derived from the orders / customers / products actions. The stub 菜单
// (menu calendar) row that used to close this data group is gone with its
// page; 周计划 in 厨房管理 replaces it.
const ordersProductsItems: SidebarNavItem[] = sectionRows(MENU_SECTION.ordersProducts, []);

// 厨房管理 / Kitchen — restored by the owner on 2026-10-03 (epic #2469 C.1,
// #2476), overturning the 2026-07-16 decision that removed the kitchen groups.
// Four entries, three of them merged: 菜品与套餐, 食材与库存 and 采购与供应商
// each land on a page whose tab strip reaches their sibling routes (the tab
// groups are in `nav-groups.ts`). 周计划, 菜品与套餐 and 食材与库存 are derived
// from the menu-plan / dish / ingredient actions; the purchase-orders list is a
// hand-written route with no action, so its row is declared here.
// Route monitoring lives on the dispatch page's map view.
const kitchenItems: SidebarNavItem[] = sectionRows(MENU_SECTION.kitchen, [
  {
    order: 40,
    titleKey: 'nav.kitchen.procurementSuppliers',
    href: '/procurement/purchase-orders',
    icon: <ShoppingBasket className="w-4 h-4" />,
    permission: PERMISSIONS.PROCUREMENT_READ,
  },
]);

// 设置 / Settings — admin role only. The section-level `adminOnly` gate is
// the single chokepoint: `ROLE_PRESETS.admin` grants everything, so the admin
// role owns every live row by construction. Row-level `permission` fields are
// intentionally omitted (no delegated-admin role exists). Route-level
// `<AdminGuard>` in `App.tsx` remains the load-bearing check for direct
// navigation. Webhook health is a tab of the 管理 › Webhooks entry — the dead
// Shopify-health/timeseries surfaces were removed in #935 PR-E2.
//
// The 系统设置 `/settings` row is GONE (#1777), not disabled: no backend
// settings surface will ever be built, so it was dead for every seat. That
// retirement is what "no greyed-out unclickable rows" asks for — it does NOT
// touch the per-seat disabled rendering in `Sidebar.tsx`, which is live
// runtime feedback for a MISSING GRANT (a row the seat cannot use yet, not a
// row nobody can ever use) and stays pinned by `Sidebar.test.tsx`. Hence the
// `disabled` field survives on the type with no current user: a permanently
// dead row is a legitimate future state, whereas a `disabled: true` row today
// would re-introduce exactly what this slice removed.
// No action backs these: an action's permission is REQUIRED, and these rows
// deliberately carry none — they are gated by the admin ROLE at the section and
// at `<AdminGuard>`. Inventing a permission key to make them derivable would
// change what the gate means, which this mechanism change must not do.
// Order per the v4 shell (owner 2026-10-03, #2476): delivery flow → integrations
// → members. Audit and webhooks moved to 管理 below.
const settingsItems: SidebarNavItem[] = sectionRows(MENU_SECTION.management, [
  // The delivery flow settings page (#2454).
  {
    order: 10,
    titleKey: 'nav.deliveryFlowSettings',
    href: '/settings/delivery-flow',
    icon: <Workflow className="w-4 h-4" />,
  },
  // Tenant admin pages (#2366).
  {
    order: 20,
    titleKey: 'nav.tenantSettings',
    href: '/settings/tenant',
    icon: <Plug className="w-4 h-4" />,
  },
  {
    order: 30,
    titleKey: 'nav.userManagement',
    href: '/users',
    icon: <UserCog className="w-4 h-4" />,
  },
]);

// 管理 / Admin — admin role only, gated exactly like 设置 above (section-level
// `adminOnly`, `<AdminGuard>` on every route, no row-level `permission`). Two
// merged entries, each with its own tab strip (`nav-groups.ts`): 审计 lands on
// the operations log and reaches both ledgers; Webhooks lands on health and
// reaches events and failures. The v4 shell (#2476) had merged them into one
// 审计与 Webhooks row of 设置 with a five-tab strip; #1364 before it had already
// folded the scattered webhook rows into the single `/webhooks` entrance.
const adminItems: SidebarNavItem[] = sectionRows(MENU_SECTION.admin, [
  {
    order: 10,
    titleKey: 'nav.audit',
    href: '/audit',
    icon: <ShieldCheck className="w-4 h-4" />,
  },
  {
    order: 20,
    titleKey: 'nav.webhooks',
    href: '/webhooks',
    icon: <Webhook className="w-4 h-4" />,
  },
]);

const docsItems: SidebarNavItem[] = sectionRows(MENU_SECTION.docs, [
  { order: 10, titleKey: 'nav.docs', href: '/docs', icon: <BookOpen className="w-4 h-4" /> },
]);

// Information architecture per spec 02-product-definition.md §2.6, the #1777
// rearrangement, the v4 shell (#2476) and the regrouping by function (#2511):
// the frontend navigates by function, not by backend context — the overall
// picture, then delivery, then the sales data, then the kitchen, then settings
// and admin.
// Array order is the visible top-to-bottom order and, after dispatch priority,
// the landing fallback order.
export const SIDEBAR_SECTIONS: readonly SidebarSection[] = [
  { labelKey: 'nav.workbench', items: workbenchItems },
  { labelKey: 'nav.delivery', items: deliveryItems },
  { labelKey: 'nav.ordersProducts', items: ordersProductsItems },
  { labelKey: 'nav.kitchen', items: kitchenItems },
  { labelKey: 'nav.management', adminOnly: true, items: settingsItems },
  { labelKey: 'nav.admin', adminOnly: true, items: adminItems },
];

/** Rows outside every group: the docs link beside the user block (#2476 S10). */
export const SIDEBAR_FOOTER_ITEMS: readonly SidebarNavItem[] = docsItems;

/**
 * A landing candidate must be a route the caller can actually ENTER, so both
 * layers of admin gating are honoured: the section-level `adminOnly` (which
 * also hides the group) and the row-level `routeAdminOnly` (which does not).
 */
export function resolveLandingPath(perms: readonly string[], isAdmin: boolean): string {
  if (perms.includes(PERMISSIONS.DISPATCH_READ)) {
    return '/dispatch';
  }

  for (const section of SIDEBAR_SECTIONS) {
    if (section.adminOnly && !isAdmin) {
      continue;
    }

    for (const item of section.items) {
      if (item.routeAdminOnly && !isAdmin) {
        continue;
      }

      if (
        item.disabled !== true &&
        item.permission !== undefined &&
        perms.includes(item.permission)
      ) {
        return item.href;
      }
    }
  }

  return '/unauthorized';
}

const ALL_ROWS: readonly SidebarNavItem[] = [
  ...SIDEBAR_SECTIONS.flatMap((section) => section.items),
  ...SIDEBAR_FOOTER_ITEMS,
];

/** Every namespace the sidebar's rows own. */
const NAV_HREFS: readonly string[] = ALL_ROWS.flatMap((row) => row.namespaces);

/**
 * Does this URL sit inside this row's namespace?
 *
 * The trailing slash is load-bearing: a bare `startsWith` would put a sibling
 * prefix such as `/dispatch-x` inside `/dispatch`.
 */
function isInsideNamespace(href: string, pathname: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Is this sidebar row the one for the page being viewed? (#2005)
 *
 * A row names a SECTION, so it stays lit anywhere inside it. Exact equality
 * was fine while every row's href was itself a landing page, but `/audit` and
 * `/webhooks` land on `/audit/operations` and `/webhooks/health` — exact
 * matching would unlight the entry the moment you arrived by clicking it.
 *
 * Containment ALONE is not enough either, and #2005 shipped it believing no
 * two hrefs were prefixes of one another. Two are: `/dispatch/jobs` sits inside
 * `/dispatch`, so the console row stayed lit while you were on the jobs list
 * and the navigation named two places at once.
 *
 * So the row that lights is the DEEPEST one containing the URL — the ordinary
 * longest-prefix rule, which needs the whole href set and cannot be decided
 * from one row. That makes nesting a supported shape rather than a hazard: a
 * row may live under another row's namespace without any coordination between
 * the two declarations.
 *
 * A merged entry (#2476) owns several namespaces — its href plus every tab
 * route in `nav-groups.ts` — so the rule runs over namespaces, not hrefs: the
 * row's deepest matching namespace must be at least as deep as every other
 * namespace that matches.
 */
export function isNavItemActive(href: string, pathname: string): boolean {
  const depth = Math.max(
    0,
    ...navNamespacesOf(href)
      .filter((namespace) => isInsideNamespace(namespace, pathname))
      .map((namespace) => namespace.length),
  );
  if (depth === 0) {
    return false;
  }
  return !NAV_HREFS.some((other) => other.length > depth && isInsideNamespace(other, pathname));
}

/**
 * The top bar's breadcrumb (#2476 T2): the group and the label of the row lit
 * for this URL. Only rows the seat can see count — a non-admin never sees the
 * settings group, so it cannot name their location. A footer row has no
 * group (`sectionKey: null`); no lit row means no breadcrumb.
 */
export function resolveBreadcrumb(
  pathname: string,
  isAdmin: boolean,
): { sectionKey: string | null; titleKey: string } | null {
  for (const section of SIDEBAR_SECTIONS) {
    if (section.adminOnly && !isAdmin) continue;
    const row = section.items.find((item) => isNavItemActive(item.href, pathname));
    if (row !== undefined) return { sectionKey: section.labelKey, titleKey: row.titleKey };
  }
  const footer = SIDEBAR_FOOTER_ITEMS.find((item) => isNavItemActive(item.href, pathname));
  return footer === undefined ? null : { sectionKey: null, titleKey: footer.titleKey };
}
